import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { svodMbFilterOptions, getDefaultSvodMbIndicators, getSvodMbDefaultDate } from "../svod-mb";
import { SvodMbFilterSettings } from "../settings/SvodMbFilterSettings";
import { SvodMbFilterSettingsForm } from "../settings-form/SvodMbFilterSettingsForm";
import { SvodMbTableDataRequest } from "../data-request/SvodMbTableDataRequest";
import { SvodMbJournalRequest } from "../journal/SvodMbJournalRequest";
import { SvodMbTableData } from "../table-data/SvodMbTableData";

export class SvodMbFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(svodMbFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        if (settings.reportName !== 'SVOD-MB') throw new Error('error_type_filter_settings')
        if (options.key !== 'SVOD-MB') throw new Error('error_type_filter_options')

        const s = settings as SvodMbFilterSettings
        const o = options as typeof svodMbFilterOptions

        const reportName = cloneDeep(o.reportNames.find(it => it.value === 'SVOD-MB')!)
        const reportType = cloneDeep(o.reportTypes.find(it => it.value === s.reportType)!)
        const classificationType = cloneDeep(o.classificationTypes.find(it => it.value === s.classificationType)!)
        const dataSource = cloneDeep(o.dataSources.find(it => it.value === s.dataSource)!)
        const periodicity = cloneDeep(o.periodicities.find(it => it.value === s.periodicity)!)
        const regionOption = cloneDeep(o.regions.find(it => it.value === s.region) ?? null)
        const budgetTypeOptions = cloneDeep(o.budgetTypes.filter(it => s.budgetTypes.includes(it.value)))
        const statusOptions = cloneDeep(o.statuses.filter(it => s.statuses.includes(it.value)))
        const measureUnitOption = cloneDeep(o.measureUnits.find(it => it.value === s.measureUnit) ?? null)

        const form: SvodMbFilterSettingsForm = {
            key: 'SVOD-MB',
            reportName,
            reportType,
            classificationType,
            dataSource,
            periodicity,
            date: s.date ? new Date(s.date) : null,
            region: regionOption,
            budgetTypes: budgetTypeOptions,
            statuses: statusOptions,
            measureUnit: measureUnitOption,
            roundUp: s.roundUp ?? true,
            roundUpTo: s.roundUpTo ?? 1,
            sortByRegionCode: s.sortByRegionCode ?? false,
            // Индикаторы — параметр, который может отсутствовать в ранее сохраненном шаблоне
            indicators: s.indicators ?? getDefaultSvodMbIndicators(),
        }
        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'SVOD-MB') throw new Error('error_type_filter_settingsForm')
        const f = form as SvodMbFilterSettingsForm

        const settings: SvodMbFilterSettings = {
            reportName: 'SVOD-MB',
            reportType: f.reportType.value,
            classificationType: f.classificationType.value,
            dataSource: f.dataSource.value,
            periodicity: f.periodicity.value,
            date: f.date ? moment(f.date).format('YYYY-MM-DD') : null,
            region: f.region?.value ?? null,
            budgetTypes: f.budgetTypes.map(it => it.value),
            statuses: f.statuses.map(it => it.value),
            measureUnit: f.measureUnit?.value ?? null,
            roundUp: f.roundUp,
            roundUpTo: f.roundUpTo,
            sortByRegionCode: f.sortByRegionCode ?? false,
            indicators: cloneDeep(f.indicators),
        }
        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(svodMbFilterOptions)

        const form: SvodMbFilterSettingsForm = {
            key: 'SVOD-MB',
            reportName: options.reportNames.find(it => it.value === 'SVOD-MB')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            date: getSvodMbDefaultDate(),
            region: null,
            budgetTypes: [],
            statuses: options.statuses.filter(it => it.value === '39'),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND') ?? null,
            roundUp: true,
            roundUpTo: 1,
            sortByRegionCode: false,
            indicators: getDefaultSvodMbIndicators(),
        }
        return form
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        if (form.key !== 'SVOD-MB') throw new Error('error_type_filter_settingsForm')
        const f = form as SvodMbFilterSettingsForm

        if (!f.date) throw new Error('error_input_date')
        if (!f.region) throw new Error('error_input_region')
        if (f.budgetTypes.length === 0) throw new Error('error_select_budget')

        const date = moment(f.date).format('YYYY-MM-DD')
        const roundUpTo = f.roundUp ? (f.roundUpTo ?? 0) : 5

        const params: SvodMbTableDataRequest = {
            date,
            region: f.region.value,
            budget_types: f.budgetTypes.map(it => it.value),
            statuses: f.statuses.map(it => Number(it.value)),
            measure_unit: f.measureUnit?.value ?? 'THOUSAND',
            round_up_to: roundUpTo,
            sort_by_region_code: f.sortByRegionCode ?? false,
            locale: i18nService.locale,
            user_id: userId,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/SVOD-MB', {
            method: 'POST',
            body: JSON.stringify(params),
        })
        if (!response.ok) throw new Error('error_api_report_data_failed')

        const responseData: SvodMbTableData = await response.json()

        const settings = this.formToSettings(cloneDeep(form))
        const journalParams: SvodMbJournalRequest = {
            reportName: 'SVOD-MB',
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            date,
            region: f.region.value,
            budget_types: f.budgetTypes.map(it => it.value),
            statuses: f.statuses.map(it => Number(it.value)),
            measure_unit: f.measureUnit?.value ?? 'THOUSAND',
            round_up_to: roundUpTo,
            sort_by_region_code: f.sortByRegionCode ?? false,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(responseData),
            journalParams,
        }
        return result
    }
}
